import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '@/store'
import { updatePreviewResponse } from '@/store/slices/preview'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { cn } from '@/utils/utils'

const Dropdown: React.FC = () => {
    const dispatch = useDispatch()
    const { selectedQuestionId } = useSelector((state: any) => state.preview)
    // Get question data from Redux store
    const question = useSelector((state: RootState) =>
        state.preview.questions.find(
            (q: any) => q.questionId === selectedQuestionId
        )
    )

    const savedResponse = useSelector(
        (state: RootState) => state.preview.previewResponses[selectedQuestionId]
    )

    // Extract options and other configuration from question
    const options = question?.config?.options || []

    const [selectedOption, setSelectedOption] = useState<string | null>(
        savedResponse?.optionId || null
    )

    // Update local state when Redux store changes
    useEffect(() => {
        if (savedResponse) {
            setSelectedOption(savedResponse.optionId || null)
        } else {
            setSelectedOption(null)
        }
    }, [savedResponse])

    useEffect(() => {
        if (selectedOption) {
            dispatch(
                updatePreviewResponse({
                    questionId: selectedQuestionId,
                    response: {
                        optionId: selectedOption,
                    },
                })
            )
        }
    }, [selectedOption, dispatch, selectedQuestionId])

    const handleOptionSelect = (optionId: string) => {
        setSelectedOption(optionId)
    }

    // If question not found, return null
    if (!question) return null

    return (
        <div className="mt-6 relative">
            <Select
                value={selectedOption || undefined}
                onValueChange={handleOptionSelect}
                key={`${selectedQuestionId}-preview`}
                id={`${selectedQuestionId}-preview`}
                className="relative"
            >
                <SelectTrigger
                    className="w-full !h-[52px] relative"
                    key={`${selectedQuestionId}-preview-trigger`}
                    id={`${selectedQuestionId}-preview-trigger`}
                >
                    <SelectValue
                        placeholder="Select an option"
                        className={cn('text-[13px] font-[450] text-[#707070]')}
                        key={`${selectedQuestionId}-preview-value`}
                        id={`${selectedQuestionId}-preview-value`}
                    />
                </SelectTrigger>
                <SelectContent
                    className="overflow-y-auto relative z-[151]"
                    key={`${selectedQuestionId}-preview-content`}
                    id={`${selectedQuestionId}-preview-content`}
                >
                    {options.map((option, index) => {
                        return (
                            <SelectItem
                                key={`${selectedQuestionId}-preview-option-${option.id}`}
                                value={option.id}
                                id={`${selectedQuestionId}-preview-option-${option.id}`}
                                className={cn(
                                    'text-[13px] font-[450] !h-[40px]',
                                    selectedOption === option.id &&
                                        'text-[#005BD3] font-[550]',
                                    option.text === '' &&
                                        'text-[#616161] italic'
                                )}
                            >
                                {option?.text !== ''
                                    ? option.text
                                    : `Response ${index + 1}`}
                            </SelectItem>
                        )
                    })}
                    {question.config.allowOther && (
                        <SelectItem
                            value="other"
                            key={`${selectedQuestionId}-preview-option-other`}
                            id={`${selectedQuestionId}-preview-option-other`}
                        >
                            Other
                        </SelectItem>
                    )}
                </SelectContent>
            </Select>
        </div>
    )
}

export default Dropdown
